import { useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  Button,
  FieldError,
  Form,
  Input,
  Label,
  Modal,
  Switch,
  TextField,
} from "@heroui/react";

import {
  useGetTariffCategoryLazy,
  useUpdateTariffCategory,
} from "@/features/tariffs/use-tariffs.ts";

interface IFormInput {
  name: string;
  key: string;
  active: boolean;
}

export function EditTariffCategoryModal({
  tariffCategoryId,
  isOpen,
  setIsOpen,
}: {
  tariffCategoryId: number;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}) {
  const { refetch, data } = useGetTariffCategoryLazy({ tariffCategoryId });
  const { mutateAsync, isPending } = useUpdateTariffCategory({
    onSuccess: () => setIsOpen(false),
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IFormInput>();

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    mutateAsync({
      id: tariffCategoryId,
      name: data.name.trim(),
      key: data.key.trim(),
      active: data.active,
      sortIndex: 0,
    }).then(() => {
      reset();
    });
  };

  useEffect(() => {
    if (isOpen) refetch();
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onOpenChange={setIsOpen}>
      <Modal.Backdrop isDismissable>
        <Modal.Container placement="center" size="lg">
          <Modal.Dialog>
            {data?.data && (
              <Form
                className="flex flex-col pt-4 pr-4"
                onSubmit={handleSubmit(onSubmit)}
              >
                <Modal.Header>
                  <Modal.Heading>Изменить категорию тарифов</Modal.Heading>
                </Modal.Header>

                <Modal.Body>
                  <TextField
                    defaultValue={data?.data.name || ""}
                    isInvalid={!!errors.name?.message}
                  >
                    <Label>Название</Label>
                    <Input
                      placeholder="Документы"
                      {...register("name", {
                        required: "Нужно заполнить поле",
                      })}
                    />
                    <FieldError>{errors.name?.message}</FieldError>
                  </TextField>

                  <TextField
                    defaultValue={data?.data.key || ""}
                    isInvalid={!!errors.key?.message}
                  >
                    <Label>Ключ переводов</Label>
                    <Input
                      {...register("key", { required: "Нужно заполнить поле" })}
                    />
                    <FieldError>{errors.key?.message}</FieldError>
                  </TextField>

                  <Controller
                    control={control}
                    defaultValue={data?.data.active}
                    name="active"
                    render={({ field }) => (
                      <Switch
                        isSelected={field.value}
                        onChange={field.onChange}
                      >
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                        <Switch.Content>
                          <Label>Активная</Label>
                        </Switch.Content>
                      </Switch>
                    )}
                  />
                </Modal.Body>

                <Modal.Footer>
                  <Button
                    variant="danger-soft"
                    onPress={() => setIsOpen(false)}
                  >
                    Отмена
                  </Button>

                  <Button isPending={isPending} type="submit" variant="primary">
                    Сохранить
                  </Button>
                </Modal.Footer>
              </Form>
            )}
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
